import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MDBContainer } from "mdb-react-ui-kit";
import { Link } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import api from "../services/api";

import "./ViewProductsPage.css";
import "./SalesIntelligencePage.css";

// Owner/admin only. All aggregation happens in the backend
// (GET /api/sales-intelligence/*) — this page only displays what it returns.

const STUCK_PAGE_SIZE = 25;

const RANGE_OPTIONS = [
  { key: "all", label: "All time" },
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "90d", label: "Last 90 days" },
  { key: "ytd", label: "This year" },
  { key: "custom", label: "Custom range" },
];

const EMPTY_FILTERS = {
  range: "all",
  dateFrom: "",
  dateTo: "",
  store: "",
  supplier: "",
  brand: "",
  category: "",
  gender: "",
  priceBand: "",
  status: "",
  market: "",
  minAge: "",
  maxAge: "",
  minSample: "15",
};

const STUCK_SORTS = [
  { key: "age", label: "Oldest first" },
  { key: "price", label: "Highest price" },
  { key: "priceAsc", label: "Lowest price" },
  { key: "views", label: "Fewest views" },
];

const eur = (value, digits = 0) =>
  value === null || value === undefined
    ? "—"
    : new Intl.NumberFormat("en-GB", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }).format(value);

const pct = (value) => (value === null || value === undefined ? "—" : `${(value * 100).toFixed(1)}%`);
const num = (value) => (value === null || value === undefined ? "—" : Number(value).toLocaleString("en-GB"));

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const getErrorMessage = (err, fallback) => {
  if (err?.response?.status === 403) {
    return "Access denied. Sales Intelligence is available to owners and admins only.";
  }
  return err?.response?.data?.message || err?.message || fallback;
};

// Only send filters that are set
const toParams = (filters) =>
  Object.fromEntries(
    Object.entries(filters).filter(
      ([key, value]) =>
        value !== "" && !(key === "range" && value === "all") && !(["dateFrom", "dateTo"].includes(key) && filters.range !== "custom"),
    ),
  );

// ================= SMALL COMPONENTS =================

const StatCard = ({ label, value, sub, tone }) => (
  <div className={`si-stat ${tone ? `si-stat--${tone}` : ""}`}>
    <span className="si-stat-label">{label}</span>
    <span className="si-stat-value">{value}</span>
    {sub && <span className="si-stat-sub">{sub}</span>}
  </div>
);

const Section = ({ title, subtitle, children, actions }) => (
  <section className="si-section">
    <div className="si-section-head">
      <div>
        <h4 className="si-section-title">{title}</h4>
        {subtitle && <p className="si-section-subtitle">{subtitle}</p>}
      </div>
      {actions}
    </div>
    {children}
  </section>
);

const StrBar = ({ value, tone }) => (
  <div className="si-str">
    <div className="si-str-bar">
      <div className={`si-str-fill si-str-fill--${tone}`} style={{ width: `${Math.min((value || 0) * 100 * 2, 100)}%` }} />
    </div>
    <span>{pct(value)}</span>
  </div>
);

const SellThroughCell = ({ row, overall }) => {
  const tone = !row.sampleOk ? "muted" : row.wilsonLow > overall ? "good" : row.wilsonHigh < overall ? "bad" : "neutral";
  return <StrBar value={row.sellThrough} tone={tone} />;
};

const GroupTable = ({ rows, overall, nameHeader = "Name", showValue = true, initialRows = 12, showAge = true }) => {
  const [expanded, setExpanded] = useState(false);
  if (!rows?.length) return <p className="si-empty">No data for the current filters.</p>;
  const visible = expanded ? rows : rows.slice(0, initialRows);
  return (
    <>
      <div className="si-table-wrap">
        <table className="si-table">
          <thead>
            <tr>
              <th>{nameHeader}</th>
              <th className="num">Listings</th>
              <th className="num">Sold</th>
              <th>Sell-through</th>
              {showValue && <th className="num">Avg sale price</th>}
              {showValue && <th className="num">Revenue / listing</th>}
              {showAge && (
                <th className="num" title="Sold vs. the average sell-through of listings of the same age (1.00 = average)">
                  Age-adjusted
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => (
              <tr key={row.key ?? row.label}>
                <td>
                  {row.label}
                  {!row.sampleOk && <span className="si-tag" title="Below the minimum sample size">small sample</span>}
                </td>
                <td className="num">{num(row.listings)}</td>
                <td className="num">{num(row.sold)}</td>
                <td>
                  <SellThroughCell row={row} overall={overall} />
                </td>
                {showValue && <td className="num">{eur(row.avgSalePrice, 2)}</td>}
                {showValue && <td className="num">{eur(row.revenuePerListing, 2)}</td>}
                {showAge && (
                  <td className="num">
                    {row.ageAdjustedIndex === null ? "—" : `${row.ageAdjustedIndex.toFixed(2)}×`}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {rows.length > initialRows && (
        <button type="button" className="btn btn-link btn-sm si-more" onClick={() => setExpanded((v) => !v)}>
          {expanded ? "Show less" : `Show all ${rows.length}`}
        </button>
      )}
    </>
  );
};

const RankList = ({ title, rows, tone }) => (
  <div className="si-rank">
    <h6 className="si-rank-title">{title}</h6>
    {rows?.length ? (
      <ol>
        {rows.map((r) => (
          <li key={r.key}>
            <span className="si-rank-name">{r.label}</span>
            <span className={`si-rank-value si-rank-value--${tone}`}>{pct(r.sellThrough)}</span>
            <span className="si-rank-n">
              {r.sold}/{r.listings} sold · avg {eur(r.avgSalePrice)}
            </span>
          </li>
        ))}
      </ol>
    ) : (
      <p className="si-empty">Not enough data (minimum sample not reached).</p>
    )}
  </div>
);

const ChartCard = ({ title, children }) => (
  <div className="si-chart">
    <h6 className="si-rank-title">{title}</h6>
    <div className="si-chart-body">{children}</div>
  </div>
);

// ================= SUPPLIER BREAKDOWN =================

// Dimensions where a segment can be split by supplier (backend SEGMENT_DIMENSIONS)
const BREAKDOWN_DIMENSIONS = ["brand", "category", "priceBand"];
const SUPPLIER_SAMPLE_OPTIONS = [5, 8, 10, 15, 20];

const RECOMMENDATION_BADGES = {
  buy_more: { label: "Buy more", className: "si-badge--good", tone: "good" },
  normal: { label: "Normal", className: "si-badge--neutral", tone: "neutral" },
  reduce: { label: "Reduce", className: "si-badge--bad", tone: "bad" },
  small_sample: { label: "Small sample", className: "si-badge--muted", tone: "muted" },
};

const SupplierBreakdownModal = ({ target, params, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [minSupplierSample, setMinSupplierSample] = useState(8);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get("/api/sales-intelligence/supplier-breakdown", {
        params: { ...params, dimension: target.dimension, key: target.key, minSupplierSample },
      })
      .then((res) => {
        if (!cancelled) setData(res.data?.data || null);
      })
      .catch((err) => {
        if (!cancelled) setError(getErrorMessage(err, "Failed to load supplier breakdown."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [target, params, minSupplierSample, reloadKey]);

  useEffect(() => {
    const onKeyDown = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const segment = data?.segment;
  const overall = data?.overall?.sellThrough || 0;
  const best = (data?.suppliers || []).filter((s) => s.recommendation === "buy_more");

  return (
    <div className="reason-modal-overlay" onClick={onClose}>
      <div
        className="reason-modal-content si-breakdown-modal"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <div className="modal-title-section">
            <h2 className="modal-product-title">{segment?.label || target.label}</h2>
            <h3 className="modal-analysis-type">
              {target.dimensionLabel} · Who should we buy it from?
            </h3>
          </div>
        </div>

        <div className="modal-body">
          {loading && !data ? (
            <div className="py-5 text-center">
              <h4>Loading supplier breakdown...</h4>
            </div>
          ) : error ? (
            <div className="alert alert-danger" role="alert">
              {error}
              <button type="button" className="btn btn-sm btn-outline-danger ms-2" onClick={() => setReloadKey((k) => k + 1)}>
                Retry
              </button>
            </div>
          ) : (
            segment && (
              <div className={loading ? "si-loading" : ""}>
                <div className="si-stats">
                  <StatCard label="Listings" value={num(segment.listings)} sub={`${num(segment.products)} products`} />
                  <StatCard label="Sold" value={num(segment.sold)} tone="good" />
                  <StatCard label="Sell-through" value={pct(segment.sellThrough)} tone="primary" />
                  <StatCard label="Overall Vinted sell-through" value={pct(overall)} sub="Same filters, all segments" />
                  <StatCard
                    label="Age-adjusted"
                    value={segment.ageAdjustedIndex === null ? "—" : `${segment.ageAdjustedIndex.toFixed(2)}×`}
                    sub="1.00× = average for listing age"
                  />
                  <StatCard
                    label="Active / stuck"
                    value={`${num(segment.active)} / ${num(segment.oldActive)}`}
                    sub={`Stuck = active > ${data.meta.oldInventoryDays / 7} weeks`}
                    tone={segment.oldActive > 0 ? "warn" : undefined}
                  />
                </div>

                <div className="si-section-head">
                  <div>
                    <h4 className="si-section-title">Best suppliers for {segment.label}</h4>
                    <p className="si-section-subtitle">
                      {best.length
                        ? `Buy more from: ${best.map((s) => s.label).join(", ")}.`
                        : "No supplier is clearly better than the others for this segment yet."}{" "}
                      Each supplier is judged only on its {segment.label} listings, not on its overall performance.
                    </p>
                  </div>
                  <div className="filter-item si-inline-filter">
                    <label htmlFor="si-min-supplier-sample">Min listings per supplier</label>
                    <select
                      id="si-min-supplier-sample"
                      className="filter-select"
                      value={minSupplierSample}
                      onChange={(e) => setMinSupplierSample(Number(e.target.value))}
                    >
                      {SUPPLIER_SAMPLE_OPTIONS.map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {data.suppliers.length === 0 ? (
                  <p className="si-empty">No supplier-attributed listings for this segment and filters.</p>
                ) : (
                  <div className="si-table-wrap">
                    <table className="si-table">
                      <thead>
                        <tr>
                          <th>Supplier</th>
                          <th className="num">Listings</th>
                          <th className="num">Sold</th>
                          <th>Sell-through</th>
                          <th className="num">Avg sale price</th>
                          <th className="num">Revenue / listing</th>
                          <th className="num" title="Sold vs. the average sell-through of listings of the same age (1.00 = average)">
                            Age-adjusted
                          </th>
                          <th className="num">Active</th>
                          <th className="num">Stuck</th>
                          <th>Recommendation</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.suppliers.map((s) => {
                          const badge = RECOMMENDATION_BADGES[s.recommendation] || RECOMMENDATION_BADGES.normal;
                          return (
                            <tr key={s.key}>
                              <td>
                                <strong>{s.label}</strong>
                                <span className="si-cell-sub">{pct(s.shareOfSegment)} of segment</span>
                              </td>
                              <td className="num">{num(s.listings)}</td>
                              <td className="num">{num(s.sold)}</td>
                              <td>
                                {/* coloured by the in-segment recommendation */}
                                <StrBar value={s.sellThrough} tone={badge.tone} />
                              </td>
                              <td className="num">{eur(s.avgSalePrice, 2)}</td>
                              <td className="num">{eur(s.revenuePerListing, 2)}</td>
                              <td className="num">
                                {s.ageAdjustedIndex === null ? "—" : `${s.ageAdjustedIndex.toFixed(2)}×`}
                              </td>
                              <td className="num">{num(s.active)}</td>
                              <td className="num">{num(s.oldActive)}</td>
                              <td className="si-rec-cell">
                                <span className={`si-badge ${badge.className}`}>{badge.label}</span>
                                <span className="si-cell-sub">{s.reason}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {data.unattributed && (
                  <p className="si-section-subtitle mt-2">
                    <strong>{num(data.unattributed.listings)}</strong> {segment.label} listings ({num(data.unattributed.sold)} sold)
                    cannot be attributed to a supplier — they are not linked to an internal product or the uploader has no
                    store name. They are excluded from the supplier comparison.
                  </p>
                )}

                <ul className="si-notes mt-3">
                  <li>
                    <strong>Buy more:</strong> {data.meta.rules.buy_more}
                  </li>
                  <li>
                    <strong>Reduce:</strong> {data.meta.rules.reduce}
                  </li>
                  <li>
                    <strong>Small sample:</strong> {data.meta.rules.small_sample}
                  </li>
                  {data.meta.notes.map((n) => (
                    <li key={n}>{n}</li>
                  ))}
                </ul>
              </div>
            )
          )}
        </div>

        <div className="modal-footer">
          <button type="button" className="modal-btn-close" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ================= PAGE =================

const SalesIntelligencePage = () => {
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [options, setOptions] = useState(null);

  const [summary, setSummary] = useState(null);
  const [problems, setProblems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [stuck, setStuck] = useState(null);
  const [stuckOffset, setStuckOffset] = useState(0);
  const [stuckSort, setStuckSort] = useState("age");
  const [stuckLoading, setStuckLoading] = useState(false);
  const [stuckError, setStuckError] = useState(null);

  const [breakdownTarget, setBreakdownTarget] = useState(null);
  const closeBreakdown = useCallback(() => setBreakdownTarget(null), []);

  const requestIdRef = useRef(0);
  const stuckRequestIdRef = useRef(0);
  const params = useMemo(() => toParams(filters), [filters]);

  useEffect(() => {
    api
      .get("/api/sales-intelligence/filters")
      .then(({ data }) => setOptions(data?.data || null))
      .catch((err) => console.error("Sales intelligence filters error:", err));
  }, []);

  const fetchDashboard = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const [summaryRes, problemsRes] = await Promise.all([
        api.get("/api/sales-intelligence/summary", { params }),
        api.get("/api/sales-intelligence/problems", { params }),
      ]);
      if (requestId !== requestIdRef.current) return;
      setSummary(summaryRes.data?.data || null);
      setProblems(problemsRes.data?.data || null);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      console.error("Sales intelligence error:", err);
      setError(getErrorMessage(err, "Failed to load Sales Intelligence. Please try again."));
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [params]);

  const fetchStuck = useCallback(async () => {
    const requestId = ++stuckRequestIdRef.current;
    setStuckLoading(true);
    setStuckError(null);
    try {
      const { data } = await api.get("/api/sales-intelligence/stuck-inventory", {
        params: { ...params, limit: STUCK_PAGE_SIZE, offset: stuckOffset, sortBy: stuckSort },
      });
      if (requestId !== stuckRequestIdRef.current) return;
      setStuck(data?.data || null);
    } catch (err) {
      if (requestId !== stuckRequestIdRef.current) return;
      setStuckError(getErrorMessage(err, "Failed to load stuck inventory."));
    } finally {
      if (requestId === stuckRequestIdRef.current) setStuckLoading(false);
    }
  }, [params, stuckOffset, stuckSort]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    fetchStuck();
  }, [fetchStuck]);

  const setDraftField = (key) => (e) => setDraft((prev) => ({ ...prev, [key]: e.target.value }));
  const applyFilters = (e) => {
    e?.preventDefault();
    setStuckOffset(0);
    setFilters(draft);
  };
  const resetFilters = () => {
    setDraft(EMPTY_FILTERS);
    setStuckOffset(0);
    setFilters(EMPTY_FILTERS);
  };

  const overview = summary?.overview;
  const overall = overview?.sellThrough || 0;
  const groups = summary?.groups || {};
  const meta = summary?.meta;

  const categoryChart = useMemo(
    () =>
      (groups.category || [])
        .filter((g) => g.sampleOk)
        .map((g) => ({ name: g.label, sellThrough: Math.round((g.sellThrough || 0) * 1000) / 10, listings: g.listings }))
        .sort((a, b) => b.sellThrough - a.sellThrough),
    [groups.category],
  );
  const priceChart = useMemo(
    () =>
      (groups.priceBand || []).map((g) => ({
        name: g.label,
        sellThrough: Math.round((g.sellThrough || 0) * 1000) / 10,
        listings: g.listings,
      })),
    [groups.priceBand],
  );
  const agingChart = useMemo(
    () => (groups.age || []).map((g) => ({ name: g.label, Sold: g.sold, Active: g.active, Draft: g.draft })),
    [groups.age],
  );

  const select = (key, label, items, allLabel) => (
    <div className="filter-item">
      <label htmlFor={`si-${key}`}>{label}</label>
      <select id={`si-${key}`} className="filter-select" value={draft[key]} onChange={setDraftField(key)}>
        <option value="">{allLabel}</option>
        {items}
      </select>
    </div>
  );

  return (
    <MDBContainer fluid className="view-products-container sales-intelligence-page">
      <h2 className="mb-1">Sales Intelligence</h2>
      <p className="si-intro">
        What is selling on Vinted, what is stuck, and where sales are lost. Live data, owner &amp; admin only.
      </p>

      {/* ================= FILTERS ================= */}
      <form className="filters-section" onSubmit={applyFilters}>
        <div className="filters-grid">
          <div className="filter-item">
            <label htmlFor="si-range">Listing date</label>
            <select id="si-range" className="filter-select" value={draft.range} onChange={setDraftField("range")}>
              {RANGE_OPTIONS.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          {draft.range === "custom" && (
            <>
              <div className="filter-item">
                <label htmlFor="si-from">From</label>
                <input id="si-from" type="date" className="filter-input" value={draft.dateFrom} onChange={setDraftField("dateFrom")} />
              </div>
              <div className="filter-item">
                <label htmlFor="si-to">To</label>
                <input id="si-to" type="date" className="filter-input" value={draft.dateTo} onChange={setDraftField("dateTo")} />
              </div>
            </>
          )}
          {select(
            "store",
            "Store",
            (options?.stores || []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label} ({o.count})
              </option>
            )),
            "All stores",
          )}
          {select(
            "supplier",
            "Supplier",
            (options?.suppliers || []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label} ({o.count})
              </option>
            )),
            "All suppliers",
          )}
          {select(
            "brand",
            "Brand",
            (options?.brands || []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label} ({o.count})
              </option>
            )),
            "All brands",
          )}
          {select(
            "category",
            "Category (from title)",
            (options?.categories || []).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            )),
            "All categories",
          )}
          {select(
            "gender",
            "Gender (from title)",
            (options?.genders || []).map((g) => (
              <option key={g} value={g}>
                {g}
              </option>
            )),
            "All",
          )}
          {select(
            "priceBand",
            "Price range",
            (options?.priceBands || []).map((b) => (
              <option key={b.key} value={b.key}>
                {b.label}
              </option>
            )),
            "All prices",
          )}
          {select(
            "status",
            "Listing status",
            (options?.statuses || []).map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            )),
            "Sold + Active + Draft",
          )}
          {select(
            "market",
            "Market",
            (options?.markets || []).map((o) => (
              <option key={o.value} value={o.value}>
                {o.label} ({o.count})
              </option>
            )),
            "All markets",
          )}
          <div className="filter-item">
            <label htmlFor="si-minage">Min age (days)</label>
            <input id="si-minage" type="number" min="0" className="filter-input" value={draft.minAge} onChange={setDraftField("minAge")} />
          </div>
          <div className="filter-item">
            <label htmlFor="si-maxage">Max age (days)</label>
            <input id="si-maxage" type="number" min="0" className="filter-input" value={draft.maxAge} onChange={setDraftField("maxAge")} />
          </div>
          <div className="filter-item">
            <label htmlFor="si-minsample" title="Groups with fewer listings are never ranked or labelled good/bad">
              Min sample size
            </label>
            <input id="si-minsample" type="number" min="1" className="filter-input" value={draft.minSample} onChange={setDraftField("minSample")} />
          </div>
          <div className="filter-item si-filter-actions">
            <button type="submit" className="btn btn-primary btn-sm">
              Apply
            </button>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={resetFilters}>
              Reset
            </button>
          </div>
        </div>
      </form>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
          <button type="button" className="btn btn-sm btn-outline-danger ms-2" onClick={fetchDashboard}>
            Retry
          </button>
        </div>
      )}

      {loading && !summary ? (
        <div className="py-5 text-center">
          <h4>Loading Sales Intelligence...</h4>
        </div>
      ) : (
        summary && (
          <div className={loading ? "si-loading" : ""}>
            {/* ================= OVERVIEW ================= */}
            <div className="si-stats">
              <StatCard label="Total Vinted listings" value={num(overview.listings)} sub={`${num(overview.products)} distinct products`} />
              <StatCard label="Sold listings" value={num(overview.sold)} tone="good" />
              <StatCard label="Active listings" value={num(overview.active)} />
              <StatCard label="Draft / removed" value={num(overview.draft)} sub="Removed from Vinted unsold" />
              <StatCard label="Sell-through rate" value={pct(overview.sellThrough)} sub="Sold ÷ all listings" tone="primary" />
              <StatCard label="Total listed value" value={eur(overview.listedValue)} />
              <StatCard label="Total sold value" value={eur(overview.soldValue)} sub="At listing price" tone="good" />
              <StatCard label="Average sale price" value={eur(overview.avgSalePrice, 2)} />
              <StatCard label="Active inventory value" value={eur(overview.activeValue)} />
              <StatCard
                label={`${meta.oldInventoryDays / 7}+ week old inventory`}
                value={num(overview.oldActiveListings)}
                sub={`${eur(overview.oldActiveValue)} still active`}
                tone={overview.oldActiveListings > 0 ? "warn" : undefined}
              />
            </div>

            {/* ================= RECOMMENDATIONS ================= */}
            <Section
              title="Recommendations"
              subtitle={`Generated from the current data. Only groups with at least ${meta.minSample} listings whose sell-through is statistically above/below the overall ${pct(
                overall,
              )} (and still so after adjusting for listing age) are labelled.`}
            >
              <div className="si-recs">
                <div className="si-rec si-rec--buy">
                  <h6>Buy more</h6>
                  {summary.recommendations.buyMore.length ? (
                    <ul>
                      {summary.recommendations.buyMore.map((r) =>
                        BREAKDOWN_DIMENSIONS.includes(r.dimension) ? (
                          <li key={`${r.dimension}-${r.key}`}>
                            <button
                              type="button"
                              className="si-rec-link"
                              title="Show which suppliers this sells best from"
                              onClick={() =>
                                setBreakdownTarget({
                                  dimension: r.dimension,
                                  dimensionLabel: r.dimensionLabel,
                                  key: r.key,
                                  label: r.label,
                                })
                              }
                            >
                              <strong>{r.label}</strong> <span className="si-rec-dim">{r.dimensionLabel}</span>
                              <span className="si-rec-cta">Suppliers →</span>
                              <small>{r.reason}</small>
                            </button>
                          </li>
                        ) : (
                          <li key={`${r.dimension}-${r.key}`}>
                            <strong>{r.label}</strong> <span className="si-rec-dim">{r.dimensionLabel}</span>
                            <small>{r.reason}</small>
                          </li>
                        ),
                      )}
                    </ul>
                  ) : (
                    <p className="si-empty">Nothing stands out significantly.</p>
                  )}
                </div>
                <div className="si-rec si-rec--reduce">
                  <h6>Reduce</h6>
                  {summary.recommendations.reduce.length ? (
                    <ul>
                      {summary.recommendations.reduce.map((r) => (
                        <li key={`${r.dimension}-${r.key}`}>
                          <strong>{r.label}</strong> <span className="si-rec-dim">{r.dimensionLabel}</span>
                          <small>{r.reason}</small>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="si-empty">Nothing is significantly below average.</p>
                  )}
                </div>
                <div className="si-rec si-rec--review">
                  <h6>Review</h6>
                  {summary.recommendations.review.length ? (
                    <ul>
                      {summary.recommendations.review.map((r, i) => (
                        <li key={`${r.type}-${i}`}>
                          <strong>{r.title}</strong>
                          <small>{r.detail}</small>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="si-empty">Nothing to review.</p>
                  )}
                </div>
              </div>
            </Section>

            {/* ================= WHAT SELLS ================= */}
            <Section title="What's selling" subtitle="Sell-through = sold listings ÷ all listings in the group. Bars are green/red only when the difference from overall is statistically significant.">
              <div className="si-charts">
                <ChartCard title={`Sell-through by category (groups ≥ ${meta.minSample} listings)`}>
                  <ResponsiveContainer width="100%" height={Math.max(220, categoryChart.length * 26)}>
                    <BarChart data={categoryChart} layout="vertical" margin={{ left: 20, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" unit="%" />
                      <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(v, n, p) => [`${v}% (${p.payload.listings} listings)`, "Sell-through"]} />
                      <Bar dataKey="sellThrough" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
                <ChartCard title="Sell-through by price range (EUR)">
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={priceChart} margin={{ right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis unit="%" />
                      <Tooltip formatter={(v, n, p) => [`${v}% (${p.payload.listings} listings)`, "Sell-through"]} />
                      <Bar dataKey="sellThrough" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </div>

              <div className="si-grid-2">
                <div>
                  <h6 className="si-rank-title">Categories (derived from title)</h6>
                  <GroupTable rows={groups.category} overall={overall} nameHeader="Category" />
                </div>
                <div>
                  <h6 className="si-rank-title">Brands</h6>
                  <GroupTable rows={groups.brand} overall={overall} nameHeader="Brand" />
                </div>
                <div>
                  <h6 className="si-rank-title">Price ranges (EUR)</h6>
                  <GroupTable rows={groups.priceBand} overall={overall} nameHeader="Price" initialRows={20} />
                </div>
                <div>
                  <h6 className="si-rank-title">Sizes</h6>
                  <GroupTable rows={groups.size} overall={overall} nameHeader="Size" showValue={false} />
                </div>
                <div>
                  <h6 className="si-rank-title">Gender (derived from title)</h6>
                  <GroupTable rows={groups.gender} overall={overall} nameHeader="Gender" />
                </div>
                <div>
                  <h6 className="si-rank-title">Vintage / retro / 90s / Y2K in title</h6>
                  <GroupTable rows={groups.vintage} overall={overall} nameHeader="Title" />
                </div>
                <div>
                  <h6 className="si-rank-title">Stores</h6>
                  <GroupTable rows={groups.store} overall={overall} nameHeader="Store" />
                </div>
                <div>
                  <h6 className="si-rank-title">Markets</h6>
                  <GroupTable rows={groups.market} overall={overall} nameHeader="Market" />
                </div>
              </div>
            </Section>

            {/* ================= TOP / WEAK ================= */}
            <Section
              title="Top performers"
              subtitle={`Ranked by the lower bound of sell-through (Wilson 95%), so small samples cannot dominate. Minimum ${meta.minSample} listings.`}
            >
              <div className="si-ranks">
                <RankList title="Brands" rows={summary.topPerformers.brand} tone="good" />
                <RankList title="Categories" rows={summary.topPerformers.category} tone="good" />
                <RankList title="Suppliers" rows={summary.topPerformers.supplier} tone="good" />
                <RankList title="Price ranges" rows={summary.topPerformers.priceBand} tone="good" />
              </div>
            </Section>

            <Section
              title="Weak performers / reduce"
              subtitle={`Ranked by the upper bound of sell-through. Minimum ${meta.minSample} listings — smaller groups are never labelled weak.`}
            >
              <div className="si-ranks">
                <RankList title="Brands" rows={summary.weakPerformers.brand} tone="bad" />
                <RankList title="Categories" rows={summary.weakPerformers.category} tone="bad" />
                <RankList title="Suppliers" rows={summary.weakPerformers.supplier} tone="bad" />
                <RankList title="Price ranges" rows={summary.weakPerformers.priceBand} tone="bad" />
              </div>
            </Section>

            {/* ================= SUPPLIERS ================= */}
            <Section
              title="Supplier performance"
              subtitle="Supplier = store of the user who uploaded the product. Sorted by sell-through lower bound."
            >
              <GroupTable
                rows={[...(groups.supplier || [])].sort(
                  (a, b) => Number(b.sampleOk) - Number(a.sampleOk) || b.wilsonLow - a.wilsonLow,
                )}
                overall={overall}
                nameHeader="Supplier"
                initialRows={50}
              />
            </Section>

            {/* ================= AGING ================= */}
            <Section title="Inventory aging" subtitle="Age = days since the listing went live on Vinted.">
              {overview.oldActiveListings > 0 && (
                <div className="alert alert-warning si-warning" role="alert">
                  <strong>{num(overview.oldActiveListings)} active listings are older than {meta.oldInventoryDays / 7} weeks</strong>{" "}
                  ({eur(overview.oldActiveValue)} listed value).
                </div>
              )}
              <div className="si-grid-2">
                <ChartCard title="Listings by age">
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={agingChart} margin={{ right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="Sold" stackId="a" fill="#10b981" />
                      <Bar dataKey="Active" stackId="a" fill="#3b82f6" />
                      <Bar dataKey="Draft" stackId="a" fill="#cbd5e1" />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
                <div className="si-table-wrap">
                  <table className="si-table">
                    <thead>
                      <tr>
                        <th>Age</th>
                        <th className="num">Listings</th>
                        <th className="num">Sold</th>
                        <th className="num">Active</th>
                        <th className="num">Sell-through</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(groups.age || []).map((g) => (
                        <tr key={g.key}>
                          <td>{g.label}</td>
                          <td className="num">{num(g.listings)}</td>
                          <td className="num">{num(g.sold)}</td>
                          <td className="num">{num(g.active)}</td>
                          <td className="num">{pct(g.sellThrough)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Section>

            {/* ================= STUCK INVENTORY ================= */}
            <Section
              title="Stuck inventory"
              subtitle={`Active listings older than ${(stuck?.minAge ?? meta.oldInventoryDays + 1) - 1} days (use "Min age" above to change). The global filters also apply. Read-only.`}
              actions={
                <select
                  aria-label="Sort stuck inventory"
                  className="filter-select si-inline-select"
                  value={stuckSort}
                  onChange={(e) => {
                    setStuckSort(e.target.value);
                    setStuckOffset(0);
                  }}
                >
                  {STUCK_SORTS.map((s) => (
                    <option key={s.key} value={s.key}>
                      {s.label}
                    </option>
                  ))}
                </select>
              }
            >
              {stuckError && <div className="alert alert-danger">{stuckError}</div>}
              {stuck && (
                <p className="si-section-subtitle">
                  {num(stuck.pagination.totalItems)} listings · {eur(stuck.totalValue)} listed value
                </p>
              )}
              <div className={`si-table-wrap ${stuckLoading ? "si-loading" : ""}`}>
                <table className="si-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Brand</th>
                      <th>Category</th>
                      <th>Size</th>
                      <th className="num">Price</th>
                      <th>Supplier</th>
                      <th>Store</th>
                      <th>Listed</th>
                      <th className="num">Age</th>
                      <th className="num">Views</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(stuck?.items || []).map((item) => (
                      <tr key={item.id}>
                        <td className="si-title-cell">{item.title}</td>
                        <td>{item.brand || "—"}</td>
                        <td>{item.category}</td>
                        <td>{item.size || "—"}</td>
                        <td className="num" title={`${item.originalPrice} ${item.originalCurrency}`}>
                          {eur(item.priceEur, 2)}
                        </td>
                        <td>{item.supplier || "—"}</td>
                        <td>{item.store || "—"}</td>
                        <td>{formatDate(item.listedAt)}</td>
                        <td className="num">{item.ageDays} d</td>
                        <td className="num">{num(item.views)}</td>
                        <td className="si-actions">
                          {item.vintedUrl && (
                            <a href={item.vintedUrl} target="_blank" rel="noopener noreferrer">
                              Vinted
                            </a>
                          )}
                          {item.internalProductId && <Link to={`/products/${item.internalProductId}`}>Product</Link>}
                        </td>
                      </tr>
                    ))}
                    {stuck && !stuck.items.length && (
                      <tr>
                        <td colSpan={11} className="si-empty">
                          No stuck listings for the current filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {stuck && stuck.pagination.totalItems > STUCK_PAGE_SIZE && (
                <div className="pagination">
                  <div className={`page-item ${stuckOffset > 0 ? "" : "disabled"}`}>
                    <button
                      type="button"
                      className="page-link"
                      disabled={stuckOffset === 0}
                      onClick={() => setStuckOffset(Math.max(0, stuckOffset - STUCK_PAGE_SIZE))}
                    >
                      Previous
                    </button>
                  </div>
                  <div className={`page-item ${stuck.pagination.hasMore ? "" : "disabled"}`}>
                    <button
                      type="button"
                      className="page-link"
                      disabled={!stuck.pagination.hasMore}
                      onClick={() => setStuckOffset(stuckOffset + STUCK_PAGE_SIZE)}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </Section>

            {/* ================= PROBLEMS ================= */}
            {problems && (
              <Section
                title="Problems & lost sales"
                subtitle="Orders: date filter only (uses when the order was scraped). Listing duplicates: whole company."
              >
                <div className="si-stats">
                  <StatCard label="Vinted orders" value={num(problems.orders.total)} />
                  {problems.orders.statusGroups.map((g) => (
                    <StatCard
                      key={g.key}
                      label={g.label}
                      value={num(g.orders)}
                      sub={`${pct(g.share)} · ${eur(g.value)}`}
                      tone={
                        g.key === "completed"
                          ? "good"
                          : ["not_sent_refunded", "cancelled_refunded"].includes(g.key)
                          ? "bad"
                          : undefined
                      }
                    />
                  ))}
                  <StatCard
                    label="Lost orders (not sent + cancelled)"
                    value={num(problems.orders.lostOrders)}
                    sub={`${pct(problems.orders.lostShare)} of orders · ${eur(problems.orders.lostValue)}`}
                    tone="bad"
                  />
                </div>

                <div className="si-grid-2">
                  <div className="si-facts">
                    <h6 className="si-rank-title">Confirmed facts</h6>
                    <ul>
                      <li>
                        <strong>{num(problems.orders.statusGroups.find((g) => g.key === "not_sent_refunded")?.orders)}</strong>{" "}
                        orders were marked "Not sent. Buyer refunded".
                      </li>
                      <li>
                        <strong>{num(problems.listings.productsListedMoreThanOnce)}</strong> of{" "}
                        {num(problems.listings.productsListed)} products have more than one Vinted listing.
                      </li>
                      <li>
                        <strong>{num(problems.listings.productsWithMultipleActiveListings)}</strong> products currently have
                        more than one active listing ({num(problems.listings.productsActiveInMultipleStores)} across different
                        stores).
                      </li>
                      <li>
                        <strong>{num(problems.listings.productsSoldMoreThanOnce)}</strong> products show more than one sold
                        listing ({num(problems.listings.extraSoldListings)} extra sold listings).
                      </li>
                      <li>
                        <strong>{num(problems.orders.repeatedTitles.orders)}</strong> orders share an item title with another
                        order ({num(problems.orders.repeatedTitles.titles)} titles, up to{" "}
                        {num(problems.orders.repeatedTitles.maxOrdersPerTitle)} orders on one title).
                      </li>
                    </ul>
                  </div>
                  <div className="si-facts si-facts--inference">
                    <h6 className="si-rank-title">Possible causes (inferences, not confirmed)</h6>
                    <ul>
                      {problems.notes.inferences.map((t) => (
                        <li key={t}>{t}</li>
                      ))}
                    </ul>
                    <h6 className="si-rank-title">Data limitations</h6>
                    <ul>
                      {problems.notes.limitations.map((t) => (
                        <li key={t}>{t}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                {problems.duplicateProducts.length > 0 && (
                  <>
                    <h6 className="si-rank-title mt-3">Products active in several stores or sold more than once</h6>
                    <div className="si-table-wrap">
                      <table className="si-table">
                        <thead>
                          <tr>
                            <th>Product</th>
                            <th>Supplier</th>
                            <th className="num">Listings</th>
                            <th className="num">Sold listings</th>
                            <th className="num">Active listings</th>
                            <th>Stores</th>
                          </tr>
                        </thead>
                        <tbody>
                          {problems.duplicateProducts.map((p) => (
                            <tr key={p.internalProductId}>
                              <td className="si-title-cell">
                                <Link to={`/products/${p.internalProductId}`}>{p.title}</Link>
                              </td>
                              <td>{p.supplier || "—"}</td>
                              <td className="num">{p.listings}</td>
                              <td className="num">{p.soldListings}</td>
                              <td className="num">{p.activeListings}</td>
                              <td>{p.stores}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </Section>
            )}

            {/* ================= NOTES ================= */}
            <Section title="How these numbers are calculated">
              <ul className="si-notes">
                {meta.notes.map((n) => (
                  <li key={n}>{n}</li>
                ))}
                {overview.unknownAgeListings > 0 && (
                  <li>
                    {num(overview.unknownAgeListings)} listings have no usable Vinted upload date — they are excluded from
                    age/date filters and shown as "Unknown listing date".
                  </li>
                )}
              </ul>
              <p className="si-generated">Generated {formatDate(meta.generatedAt)} {new Date(meta.generatedAt).toLocaleTimeString("en-GB")}</p>
            </Section>
          </div>
        )
      )}

      {breakdownTarget && (
        <SupplierBreakdownModal target={breakdownTarget} params={params} onClose={closeBreakdown} />
      )}
    </MDBContainer>
  );
};

export default SalesIntelligencePage;
